import {
    Box,
    Button,
    Divider,
    Link,
    Paper,
    type PaperProps,
    Stack,
    Typography,
    colorsPalette,
    styled,
    useTheme,
} from '@lingoda/ui';
import { CaretLeftIcon, CaretRightIcon } from '@lingoda/ui/icons/lingodaIcons';
import { useDispatch } from '@lingoda/hooks';
import { DialogComponent, showDialog } from '@lingoda/dialogs';
import { trans, transChoice } from '@lingoda/i18n';
import { useStudentBookingClassStatusSuspenseQuery } from '@lingoda/monolith-graphql';
import { studentBookingPath } from '@lingoda/router';
import { StyledHorizontalLine } from '../common/StyledHorizontalLine';
import { Chart } from './Chart';
import { ChartSkeleton } from './ChartSkeleton';
import type { ChartItem } from './Chart';

export interface WeeklyProgressLayoutProps {
    loading: boolean;
    currentGoal: number | null;
    monthRange: string;
    onNavigationLeft?: () => void;
    onNavigationRight?: () => void;
    chartItems: ChartItem[];
    page: number;
    paperProps?: PaperProps;
}

export const WeeklyProgressLayout = ({
    loading,
    currentGoal,
    monthRange,
    onNavigationLeft,
    onNavigationRight,
    chartItems,
    page,
    paperProps,
}: WeeklyProgressLayoutProps) => {
    const theme = useTheme();
    const { data } = useStudentBookingClassStatusSuspenseQuery();
    const { hasBookedClass, completedClasses } = data.student;
    const hasCompletedClasses = completedClasses > 0;

    const dispatch = useDispatch();
    const handleOpenSetGoalDialog = () => {
        dispatch(
            showDialog(DialogComponent.SetGoalWeeklyClasses, {
                closable: true,
                notifyAfterSubmit: true,
                currentGoal,
            }),
        );
    };

    return (
        <Paper data-cy="Weekly progress" {...paperProps}>
            <HeaderBox>
                <Stack direction="row" justifyContent="space-between">
                    <Stack direction="column" spacing={1}>
                        <Typography variant="h3">
                            {trans('class-attendance-header', {}, 'student-common')}
                        </Typography>
                        {currentGoal ? (
                            <Typography variant="body3" htmlColor="text.secondary">
                                {[
                                    trans('weekly-progress-goal', {}, 'student-common'),
                                    ': ',
                                    transChoice(
                                        'weekly-progress-subtitle',
                                        currentGoal,
                                        { goal: currentGoal },
                                        'student-common',
                                    ),
                                ].join('')}
                            </Typography>
                        ) : null}
                    </Stack>
                    <Link to={'#'} onClick={handleOpenSetGoalDialog} underline="none" py={0.75}>
                        <Typography variant="body3">
                            {currentGoal
                                ? trans('weekly-progress-edit-goal', {}, 'student-common')
                                : trans('weekly-progress-set-goal', {}, 'student-common')}
                        </Typography>
                    </Link>
                </Stack>
            </HeaderBox>
            {!hasBookedClass || !hasCompletedClasses ? (
                <Box
                    display="flex"
                    px={3}
                    pb={3}
                    pt={1.5}
                    data-testid="weekly-progress-booking-status"
                >
                    <StyledHorizontalLine />
                    <Box>
                        <Typography
                            variant="subtitle2"
                            alignSelf="center"
                            htmlColor="text.secondary"
                        >
                            {hasBookedClass
                                ? trans(
                                      'you-ll-see-your-attendance-after-your-first-class',
                                      {},
                                      'student-today',
                                  )
                                : trans(
                                      'no-attendance-record-yet-take-your-first-step',
                                      {},
                                      'student-today',
                                  )}
                        </Typography>
                        {!hasBookedClass && (
                            <Box mt={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    component={Link}
                                    href={studentBookingPath()}
                                >
                                    {trans('book-first-class-title', {}, 'student-today')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : (
                <>
                    <Divider sx={{ marginTop: 2 }} />
                    <NavigationBox>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body3" htmlColor="text.secondary">
                                {monthRange}
                            </Typography>
                            <Stack direction="row">
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={onNavigationLeft}
                                    disabled={!onNavigationLeft}
                                    sx={{ padding: 1 }}
                                >
                                    <CaretLeftIcon
                                        size={12}
                                        color={
                                            onNavigationLeft
                                                ? theme.palette.text.secondary
                                                : colorsPalette.blackAlpha[20]
                                        }
                                    />
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={onNavigationRight}
                                    disabled={!onNavigationRight}
                                    sx={{ padding: 1 }}
                                >
                                    <CaretRightIcon
                                        size={12}
                                        color={
                                            onNavigationRight
                                                ? theme.palette.text.secondary
                                                : colorsPalette.blackAlpha[20]
                                        }
                                    />
                                </Button>
                            </Stack>
                        </Stack>
                    </NavigationBox>
                    <ContentBox>
                        <Stack spacing={3}>
                            {loading ? <ChartSkeleton /> : <Chart items={chartItems} page={page} />}
                        </Stack>
                    </ContentBox>
                </>
            )}
        </Paper>
    );
};

const HeaderBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3, 3, 0),
}));

const NavigationBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 1.5, 0, 3),
}));

const ContentBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0, 3, 3, 3),
}));
